import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import { InvoiceReadingApi } from "src/api/generated/erp/sales/invoicing/api/invoiceReadingApi.ts";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns";
import i18n from "i18next";
import { getInvoiceTypeLabels } from "src/api/generated/erp/db/types/enums/invoiceType.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { InvoiceOperationApi } from "src/api/generated/erp/sales/invoicing/api/invoiceOperationApi.ts";
import { downloadFile } from "src/utils/fileDownloading.ts";
import {
	faArrowRight,
	faCheck,
	faExternalLink,
	faMoneyCheck,
	faPrint,
	faTurnDown,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { getInvoiceStateLabels } from "src/api/generated/erp/db/types/enums/invoiceState.ts";
import { InvoiceView } from "src/api/generated/erp/db/types/tables/invoiceView.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";

import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CreateRefundInvoiceForm } from "src/components/views/erp/sales/invoicing/CreateRefundInvoiceForm.tsx";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { InvoiceForm } from "src/components/views/erp/sales/invoicing/InvoiceForm.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { InspectInvoiceView } from "src/components/views/erp/sales/invoicing/InspectInvoiceView.tsx";
import { RefreshableElementProps, setRefreshRefValue } from "src/utils/useRefreshRef.ts";
import { InvoiceCreationApi } from "src/api/generated/erp/sales/invoicing/api/invoiceCreationApi.ts";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { InvoicePaymentEventsDataGrid } from "src/components/views/erp/sales/invoicing/InvoicePaymentEventsDataGrid.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { renderMoneyCellWithRowCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface InvoicesDataGridBaseProps extends RefreshableElementProps {
	customerOrderId?: number;
	customerId?: number;
	hideColumns?: [keyof InvoiceView];
	hideSearchField?: boolean;
}

export const InvoicesDataGridBase = ({
	hideSearchField,
	customerOrderId,
	customerId,
	refreshRef,
}: InvoicesDataGridBaseProps) => {
	const { dataGridProps, refreshData, currentParams, onlySelectedRow, selectedRows } = useServerSideDataGridModel({
		gridId: "74C4010104EDA4FB",
		fetchData: InvoiceReadingApi.getInvoices,
		getRowId: (row) => row.invoiceId,
		customFunctionUiActionView: "INVOICES",
		initialParams: {
			customerOrderId: customerOrderId,
			customerId: customerId,
			onlyInvoicesNotInAccounting: false,
			searchQuery: "",
		},
	});

	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();
	const hasAccountingIntegration = tenantConfig.erp.accountingIntegrationEnabled;
	const editable = hasPermission(Permissions.Sales.Invoices.MANAGE);

	const { openDialog } = useGenericDialog();
	const confirm = useConfirmDialog();

	setRefreshRefValue(refreshRef, refreshData);

	return (
		<ControlledAsyncFormCrudDataGrid<InvoiceView>
			getRowClassNames={({ row }) => (row.invoiceState === "PAID" ? dataGridRowClassNames.green : undefined)}
			columns={[
				integerColumn({
					field: "invoiceId",
					headerName: i18n.t("number_shortened"),
					width: 100,
				}),
				textColumn({
					field: "referenceNumber",
					headerName: i18n.t("reference_number"),
					width: 140,
				}),
				enumColumn({
					field: "invoiceType",
					headerName: i18n.t("type"),
					enumLabels: getInvoiceTypeLabels(),
				}),
				dateColumn({
					field: "invoiceDate",
					headerName: i18n.t("invoice_date"),
					width: 120,
				}),
				enumColumn({
					field: "invoiceState",
					headerName: i18n.t("state"),
					enumLabels: getInvoiceStateLabels(),
				}),
				integerColumn({
					field: "customerOrderId",
					headerName: i18n.t("order_number"),
					width: 120,
				}),
				dateColumn({
					field: "deliveryDate",
					headerName: i18n.t("delivery_date"),
					width: 120,
				}),
				textColumn({
					field: "externalId",
					headerName: i18n.t("external_id"),
					width: 150,
				}),
				textColumn({
					field: "customerName",
					headerName: i18n.t("customer"),
					width: 150,
				}),
				textColumn({
					field: "customerBusinessId",
					headerName: i18n.t("customer_business_id"),
					width: 150,
				}),
				textColumn({
					field: "siteName",
					headerName: i18n.t("site"),
				}),
				textColumn({
					field: "currencyCode",
					headerName: i18n.t("currency"),
					width: 100,
				}),
				dateTimeColumn({
					field: "createdAt",
					headerName: i18n.t("created_at"),
					width: 130,
				}),
				textColumn({
					field: "createdByUserName",
					headerName: i18n.t("created_by"),
				}),
				textColumn({
					field: "vatHandlingTypeName",
					headerName: i18n.t("vat_handling_type"),
					width: 120,
				}),
				moneyColumn({
					field: "totalAmount",
					headerName: i18n.t("sum_without_vat"),
					width: 120,
					renderCell: renderMoneyCellWithRowCurrency((row) => row.currencyCode),
				}),
				moneyColumn({
					field: "totalVat",
					headerName: i18n.t("vat"),
					renderCell: renderMoneyCellWithRowCurrency((row) => row.currencyCode),
				}),
				moneyColumn({
					field: "totalAmountWithVat",
					headerName: i18n.t("sum_with_vat"),
					width: 120,
					renderCell: renderMoneyCellWithRowCurrency((row) => row.currencyCode),
				}),
			]}
			form={{
				dialogSize: "md",
				dialogTitle: i18n.t("invoice"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.sales.invoice.formDialog"),
					pathSegments: row != null ? [invoicePathSegment(row)] : [i18n.t("invoice")],
					data: {
						invoiceId: row?.invoiceId ?? null,
						customerOrderId: row?.customerOrderId ?? null,
						customerId: row?.customerId ?? null,
					},
				}),
				component: ({ row, onCompleted, onFormEdited }) => {
					if (!row) return;
					return (
						<InvoiceForm
							invoiceId={row.invoiceId}
							onCompleted={(result) => onCompleted(result)}
							onFormEdited={onFormEdited}
						/>
					);
				},
				addRowEnabled: false,
				editEnabled: editable,
			}}
			onRowDoubleClick={({ row }) => {
				openDialog(() => ({
					title: i18n.t("invoice"),
					agentContext: invoiceDialogAgentContext(row),
					content: <InspectInvoiceView invoice={row} />,
				}));
			}}
			actionBarComponents={
				<>
					{hideSearchField !== true && (
						<AavoTextField
							label={i18n.t("search")}
							onSubmit={async (input) => {
								await refreshData({ searchQuery: input });
							}}
						/>
					)}
					<AavoButton
						variant={"outlined"}
						icon={faExternalLink}
						label={i18n.t("open")}
						disabled={onlySelectedRow === null}
						onClick={() => {
							if (onlySelectedRow == null) return;
							openDialog(() => ({
								title: i18n.t("invoice"),
								agentContext: invoiceDialogAgentContext(onlySelectedRow),
								content: <InspectInvoiceView invoice={onlySelectedRow} />,
							}));
						}}
					/>
					<AsyncButton
						icon={faPrint}
						label={i18n.t("print")}
						variant={"outlined"}
						disabled={selectedRows.length === 0}
						onClick={async () => {
							const fileHandles = await InvoiceOperationApi.printInvoices({
								invoiceIds: selectedRows.map((row) => row.invoiceId),
							});
							fileHandles.forEach((fileHandle) => downloadFile(fileHandle));
							await refreshData();
						}}
					/>
					{hasAccountingIntegration && editable && (
						<AsyncButton
							icon={faArrowRight}
							label={i18n.t("copy_to_accounting")}
							variant={"outlined"}
							disabled={selectedRows.length === 0 || selectedRows.some((row) => row.externalId != null)}
							onClick={async () => {
								const confirmed = await confirm({
									message: i18n.t("copy_invoices_to_accounting_confirm"),
								});
								if (!confirmed) return;

								await InvoiceOperationApi.copyInvoicesToAccounting({
									invoiceIds: selectedRows.map((row) => row.invoiceId),
								});
								await refreshData();
							}}
						/>
					)}
					<AavoCheckbox
						label={i18n.t("only_invoices_not_in_accounting")}
						checked={currentParams.onlyInvoicesNotInAccounting}
						onChange={async (checked) => {
							await refreshData({ onlyInvoicesNotInAccounting: checked });
						}}
					/>
				</>
			}
			rowContextMenuComponents={({ row, onlySingleRowSelected, allSelectedRows }) => [
				onlySingleRowSelected && row.customerOrderId != null && customerOrderId == null && (
					<OpenCustomerOrderButton key={"open_customer_order"} customerOrderId={row.customerOrderId} />
				),
				onlySingleRowSelected && row.invoiceType === "CHARGE" && editable && (
					<AsyncMenuButton
						key={"create_refund_invoice"}
						label={i18n.t("create_a_refund_invoice")}
						icon={faTurnDown}
						onClick={() => {
							openDialog(({ closeDialog }) => ({
								title: i18n.t("refund_invoice"),
								size: "lg",
								agentContext: {
									key: defineAgentUiContextKey("erp.sales.refundInvoice.dialog"),
									pathSegments: [invoicePathSegment(row), i18n.t("refund_invoice")],
									data: {
										chargeInvoiceId: row.invoiceId,
										customerOrderId: row.customerOrderId ?? null,
										customerId: row.customerId ?? null,
									},
								},
								content: (
									<CreateRefundInvoiceForm
										chargeInvoiceId={row.invoiceId}
										onCompleted={async (result) => {
											await closeDialog();
											if (result.type === "success") {
												await refreshData();
											}
										}}
									/>
								),
							}));
						}}
					/>
				),
				onlySingleRowSelected && row.invoiceType === "CHARGE" && editable && (
					<AsyncMenuButton
						key={"cancelInvoice"}
						label={i18n.t("cancel_invoice")}
						icon={faUndo}
						onClick={async () => {
							const confirmed = await confirm({
								title: i18n.t("cancel_invoice"),
								message: i18n.t("cancel_invoice_confirmation"),
							});
							if (!confirmed) return;

							await InvoiceCreationApi.cancelInvoice({
								chargeInvoiceId: row.invoiceId,
							});
							await refreshData();
						}}
					/>
				),
				editable && allSelectedRows.some((row) => row.externalId == null) && (
					<AsyncMenuButton
						key={"mark_as_copied_to_accounting"}
						icon={faCheck}
						label={i18n.t("mark_as_copied_to_accounting")}
						onClick={async () => {
							const confirmed = await confirm({
								message: i18n.t("mark_as_copied_to_accounting_confirmation"),
							});
							if (!confirmed) return;

							await InvoiceOperationApi.markInvoicesAsCopiedToAccounting({
								invoiceIds: allSelectedRows.map((row) => row.invoiceId),
							});
							await refreshData();
						}}
					/>
				),
				onlySingleRowSelected && hasAccountingIntegration && row.externalId != null && (
					<AsyncMenuButton
						key={"payment_events"}
						icon={faMoneyCheck}
						label={i18n.t("payment_events")}
						onClick={async () => {
							openDialog(() => ({
								title: i18n.t("payment_events"),
								size: "xl",
								agentContext: {
									key: defineAgentUiContextKey("erp.sales.invoicePaymentEvents.dialog"),
									pathSegments: [invoicePathSegment(row), i18n.t("payment_events")],
									data: {
										invoiceId: row.invoiceId,
										externalId: row.externalId ?? null,
									},
								},
								content: (
									<InvoicePaymentEventsDataGrid
										invoiceId={row.invoiceId}
										currencyCode={row.currencyCode}
									/>
								),
							}));
						}}
					/>
				),
				onlySingleRowSelected && (
					<OpenObjectChangeLogButton
						key={"open_change_log"}
						objectRef={{
							objectType: "INVOICE",
							objectId: row.invoiceId,
						}}
					/>
				),
			]}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.invoices.grid"),
				data: {
					customerOrderId: customerOrderId ?? null,
					customerId: customerId ?? null,
				},
				rowFields: ["invoiceId", "customerOrderId", "customerId", "referenceNumber", "externalId"],
				selectedRowPathSegment: invoicePathSegment,
			}}
			{...dataGridProps}
		/>
	);
};

function invoiceDialogAgentContext(row: InvoiceView) {
	return {
		key: defineAgentUiContextKey("erp.sales.invoice.dialog"),
		pathSegments: [invoicePathSegment(row)],
		data: {
			invoiceId: row.invoiceId,
			customerOrderId: row.customerOrderId ?? null,
			customerId: row.customerId ?? null,
		},
	};
}

function invoicePathSegment(row: InvoiceView): string {
	return `${i18n.t("invoice")} ${row.invoiceId}`;
}
